import React, { useCallback, useEffect, useState } from "react";
import DistributorApi from "../../utils/api/DistributorApi";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "../../components/table/Table.module.css";
import historyStyles from "./History.module.css";
import Pagination from "../../components/list/Pagination";
import { fetchWithSearchFallback } from "../../utils/searchFallback";
import useDebouncedValue from "../../hooks/useDebouncedValue";

function UsedSoldPins() {
  const [pins, setPins] = useState([]);
  const [filteredPins, setFilteredPins] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [loadingPins, setLoadingPins] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [serverSearchAvailable, setServerSearchAvailable] = useState(null);
  const [usingServerSearch, setUsingServerSearch] = useState(false);

  const token = useSelector((state) => state.userData.loginData.token);

  const fetchPins = useCallback(async () => {
    try {
      setLoadingPins(true);
      const result = await fetchWithSearchFallback({
        search: debouncedSearch,
        serverSearchAvailable,
        fetchPage: ({ search }) => DistributorApi.getUsedSoldPinList({ token, page: currentPage, search }),
      });
      setServerSearchAvailable(result.serverSearchAvailable);
      setUsingServerSearch(result.usedServerSearch);
      const data = result.response.data?.data;
      const rows = data?.data ?? [];
      setPins(rows);
      setLastPage(data?.last_page ?? null);
      setHasNextPage(!!data?.next_page_url);
      setHasPrevPage(!!data?.prev_page_url);
    } catch (error) {
      toast.error(error?.response?.data?.message ?? "Failed to load pins", {
        position: "top-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "dark",
      });
    } finally {
      setLoadingPins(false);
    }
  }, [token, currentPage, debouncedSearch, serverSearchAvailable]);

  useEffect(() => {
    fetchPins();
  }, [fetchPins]);

  useEffect(() => {
    if (searchInput === "" || usingServerSearch) {
      setFilteredPins(pins);
    } else {
      const q = searchInput.toLowerCase();
      setFilteredPins(
        pins.filter(
          (p) =>
            p?.pin?.toLowerCase().includes(q) ||
            p?.type?.toLowerCase().includes(q) ||
            p?.platform?.toLowerCase().includes(q)
        )
      );
    }
  }, [searchInput, pins, usingServerSearch]);

  const handleRowClick = (index) => {
    setSelectedRow(selectedRow === index ? null : index);
  };

  return (
    <div>
      <ToastContainer />

      <div className={historyStyles.filter_wrapper}>
        <input
          type="text"
          value={searchInput}
          onChange={(e) => { setSearchInput(e.target.value); setUsingServerSearch(false); setCurrentPage(1); }}
          placeholder={serverSearchAvailable === false ? "Filter this page by pin, type, platform..." : "Search pins"}
          className={historyStyles.filter_input}
        />
      </div>

      {/* Table header row */}
      <div className={styles.table_header}>
        <div className={styles.table_column}>
          <p>S/N</p>
        </div>
        <div className={styles.table_column}>
          <p>Pin</p>
        </div>
        <div className={styles.table_column}>
          <p>Platform</p>
        </div>
        <div className={styles.table_column}>
          <p>Type</p>
        </div>
        <div className={styles.table_column}>
          <p>Status</p>
        </div>
      </div>

      <div className={styles.table_inner}>
        {loadingPins ? (
          <>
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className={historyStyles.skeleton}
                style={{ width: "100%", height: "45px", borderRadius: "4px" }}
              />
            ))}
          </>
        ) : filteredPins.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              padding: "48px 24px",
              width: "100%",
              color: "#9ca3af",
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
            }}
          >
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="#D0D5DD"
                strokeWidth="1.5"
              />
              <path
                d="M8 12h8M12 8v8"
                stroke="#D0D5DD"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <p>No used sold pins found</p>
          </div>
        ) : (
          filteredPins.map((item, index) => (
            <div key={item.id} className={styles.table_header_wrapper}>
              <div
                className={styles.table_header}
                onClick={() => handleRowClick(index)}
                style={{ cursor: "pointer" }}
              >
                <div className={styles.table_column}>
                  <p>{index + 1}</p>
                </div>
                <div className={styles.table_column}>
                  <p style={{ fontFamily: "monospace", fontWeight: 700 }}>
                    {item.pin ?? "—"}
                  </p>
                </div>
                <div className={styles.table_column}>
                  <p>{item.platform ?? "—"}</p>
                </div>
                <div className={styles.table_column}>
                  <p>{item.type ?? "—"}</p>
                </div>
                <div className={styles.table_column}>
                  <svg
                    width="4"
                    height="17"
                    viewBox="0 0 4 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.64783 2.47626C2.64783 2.83207 2.3594 3.12049 2.00361 3.12049C1.64781 3.12049 1.35938 2.83206 1.35938 2.47626C1.35938 2.12046 1.64781 1.83203 2.00361 1.83203C2.3594 1.83203 2.64783 2.12046 2.64783 2.47626ZM2.64789 14.5203C2.64789 14.8761 2.35946 15.1645 2.00366 15.1645C1.64786 15.1645 1.35943 14.8761 1.35943 14.5203C1.35943 14.1645 1.64786 13.876 2.00366 13.876C2.35947 13.876 2.64789 14.1645 2.64789 14.5203ZM2.64783 8.52025C2.64783 8.87605 2.3594 9.16448 2.00361 9.16448C1.64781 9.16448 1.35938 8.87605 1.35938 8.52025C1.35938 8.16445 1.64781 7.87602 2.00361 7.87602C2.3594 7.87602 2.64783 8.16445 2.64783 8.52025Z"
                      stroke="#36363E"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </div>

              {selectedRow === index && (
                <div className={styles.row_details}>
                  <div className={styles.row_details_time}>
                    <h1>{item.pin ?? "—"}</h1>
                    <p>Platform: {item.platform ?? "—"}</p>
                    <p>Type: {item.type ?? "—"}</p>
                  </div>
                  <div
                    className={
                      item.status === "ACTIVE"
                        ? styles.row_details_status
                        : styles.row_details_status_error
                    }
                  >
                    <p
                      style={{
                        color:
                          item.status === "ACTIVE" ? "#13BE3E" : "#FF0B0B",
                      }}
                    >
                      {item.status ?? "—"}
                    </p>
                  </div>
                  <div></div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <Pagination
        pagination={{ page: currentPage, lastPage: lastPage || 1, total: 0, hasNextPage, hasPreviousPage: hasPrevPage }}
        onPageChange={setCurrentPage}
        itemLabel="used PINs"
      />
    </div>
  );
}

export default UsedSoldPins;
